// DissertativeWorkspaceView — Modo Estudo / Simulação no padrão dissertativo.
//
// Renderiza dentro do <main> do shell, igual ao StudyWorkspaceView. Reusa
// QuestionCard (com tipo="essay") + QuestionListSidebar + AiAssistant —
// visual e controles 100% idênticos a uma prova dissertativa NATIVA do app.
// A única diferença é que as questões vêm de payload.questoesDissertativas
// (geradas pelo prompt alternativa-para-dissertativa.md) e o ID começa
// com "dissai-<idx>" — o backend bank-essay reconhece esse prefixo e
// sintetiza a QuestaoLike no formato esperado, permitindo o autosave +
// correção IA padrão funcionarem sem código novo.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Flag,
  Grid3x3,
  Hourglass,
  List,
  Loader2,
  Pencil,
  RotateCcw,
  SlidersHorizontal,
  Sparkles,
  X
} from "lucide-react";
import {
  WorkspaceHeader,
  type WorkspaceHeaderAction
} from "./WorkspaceHeader";
import { Skeleton } from "@app/ui/components/skeleton";
import { AiAssistant } from "./AiAssistant";
import { QuestionCard } from "./QuestionCard";
import { QuestionListSidebar, type QListStatus, applyAgrupamento } from "./QuestionListSidebar";
import { uiPrefs, toggleAiAssistantOpen, toggleQuestionListCollapsed } from "./uiPrefs";
import type { EssayResponseState, QuestaoStudy } from "./api";
import { aiCorrectionPrefs } from "./aiPrefs";
import { fireCorrectAnimation } from "./animations";

export type DissertativeMode = "estudo" | "prova";
type DissStatusKey = "pendente" | "respondida" | "avaliada";

interface DissertativeQuestionRaw {
  originalQuestionIdx: number;
  tema: string;
  caso: string;
  imagens: unknown[];
  tabelas: unknown[];
  enunciado: string;
  respostaSalva: string;
  tempoMs?: number;
  aiGrade: {
    score: number;
    feedback: string;
    pontosAtendidos: string[];
    pontosFaltando: string[];
    armadilhasCometidas: string[];
    gradedAt: string;
    /** Nota antes do desconto de norma culta (presente quando houve desconto). */
    scoreSemDesconto?: number | null;
    /** Detalhamento do desconto de norma culta quando ativo. */
    descontoNormaCulta?: {
      pontos: number;
      erros: Array<{ trecho: string; problema: string; correto: string }>;
    } | null;
    /** Modo usado nesta correção, pra renderizar badge. */
    strictnessUsed?: "standard" | "strict";
    grammarModeUsed?: "off" | "judgmental" | "fixed";
  } | null;
  gabarito?: {
    resposta_modelo: string;
    pontos_esperados: Array<{ ponto: string; peso: number }>;
    criterios: string[];
    armadilhas_comuns: string[];
  } | null;
}

interface ApiResponse {
  bank: { id: string; name: string; dissertativeCount: number };
  questoes: DissertativeQuestionRaw[];
  finalized?: boolean;
  mode: DissertativeMode;
  stats: {
    respondidas: number;
    total: number;
    avaliadas: number;
    scoreMedia: number | null;
  };
}

// ===== Signals globais =====

export const dissertativeWorkspace = signal<{
  bankId: string;
  mode: DissertativeMode;
} | null>(null);

/** Nome do banco da dissertativa atualmente aberta. Atualizado pelo `reload()`
 *  quando o GET volta. Permite ao breadcrumb do App mostrar "Bancos / <nome> /
 *  Dissertativa" sem precisar reler o payload no nível de cima. */
export const dissertativeWorkspaceBankName = signal<string | null>(null);

let onExitCallback: (() => void) | null = null;
export function onDissertativeWorkspaceExit(cb: () => void) {
  onExitCallback = cb;
}

export function enterDissertativeWorkspace(bankId: string, mode: DissertativeMode) {
  dissertativeWorkspace.value = { bankId, mode };
}

export function exitDissertativeWorkspace() {
  dissertativeWorkspace.value = null;
  dissertativeWorkspaceBankName.value = null;
  if (onExitCallback) onExitCallback();
}

export const dissertativeActive = dissertativeWorkspace;

// ===== Componente principal =====

export function DissertativeWorkspaceView() {
  const ws = dissertativeWorkspace.value;
  if (!ws) return null;
  return <Inner key={`${ws.bankId}:${ws.mode}`} bankId={ws.bankId} mode={ws.mode} />;
}

function Inner({ bankId, mode }: { bankId: string; mode: DissertativeMode }) {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [bank, setBank] = useState<ApiResponse["bank"] | null>(null);
  const [raw, setRaw] = useState<DissertativeQuestionRaw[]>([]);
  const [stats, setStats] = useState<ApiResponse["stats"] | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [finalized, setFinalized] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [agrupamento, setAgrupamento] = useState<"tema" | "fonte">("fonte");

  // === Cronômetros client-side (Questão atual + Total) ===
  // Equivalente ao StudyTimers do StudyWorkspace. Ephemeral por enquanto
  // (não persiste no backend), mas dá ao aluno a noção de tempo enquanto
  // escreve. Karol 2026-05-29: "header dissertativa sem cronômetro".
  // Ticka 1× por segundo enquanto a aba está visível. q.id corrente é
  // mantido em ref pra não recriar o setInterval a cada navegação.
  const [essayTempos, setEssayTempos] = useState<Record<string, number>>({});
  const [tempoTotalMs, setTempoTotalMs] = useState(0);
  const currentQidRef = useRef<string | null>(null);
  useEffect(() => {
    const tick = window.setInterval(() => {
      if (document.hidden) return;
      const qid = currentQidRef.current;
      if (!qid) return;
      setEssayTempos((prev) => ({ ...prev, [qid]: (prev[qid] ?? 0) + 1000 }));
      setTempoTotalMs((t) => t + 1000);
    }, 1000);
    return () => window.clearInterval(tick);
  }, []);

  // === Filtros / Mapa / Desempenho — equalizar com StudyWorkspace ===
  // Karol 2026-05-29: "header dissertativa precisa de Filtros, Mapa,
  // Desempenho como tem no Estudo".
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [performanceOpen, setPerformanceOpen] = useState(false);
  // Filtro de status: subset de {pendente, respondida, avaliada}. Vazio = todos.
  const [filtroStatus, setFiltroStatus] = useState<Set<DissStatusKey>>(
    new Set()
  );
  // Filtro de temas: subset dos temas únicos do banco. Vazio = todos.
  const [filtroTemas, setFiltroTemas] = useState<Set<string>>(new Set());
  // Em modo Estudo o gabarito é gated por toggle no QuestionCard (showExplanation).
  // Em modo Prova só libera após finalize.

  async function reload() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const r = await fetch(
        `/api/student/bank-dissertative?id=${encodeURIComponent(bankId)}&mode=${mode}`,
        { credentials: "include" }
      );
      if (!r.ok) {
        let m = `HTTP ${r.status}`;
        try {
          const b = (await r.json()) as { message?: string };
          m = b.message ?? m;
        } catch { /* ignore */ }
        throw new Error(m);
      }
      const data = (await r.json()) as ApiResponse;
      setBank(data.bank);
      setRaw(data.questoes);
      setStats(data.stats);
      setFinalized(!!data.finalized);
      dissertativeWorkspaceBankName.value = data.bank.name;
      setPhase("ready");
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId, mode]);

  // === Conversão DissertativaIA → QuestaoStudy + EssayResponseState ===
  // Mapeia o array do backend pro formato que QuestionCard/QuestionListSidebar
  // consomem. O qid `dissai-<idx>` ativa o caminho especial no bank-essay
  // backend (autosave/grade IA funcionam sem mudanças).
  const questoesStudy: QuestaoStudy[] = useMemo(() => {
    return raw.map((q, i) => {
      // Pontos esperados e critérios fazem parte do GABARITO — viram
      // criterios[] (renderizado SÓ dentro do bloco gabarito do
      // EssayQuestionBody, que é gated por "Ver gabarito" no Estudo
      // e por finalizar no modo Prova). Armadilhas viram competencias[].
      // Direcionadores fica vazio: o enunciado da própria questão já
      // serve como comando — não há "proposta extra" pra exibir.
      const pontosFormatados = (q.gabarito?.pontos_esperados ?? []).map(
        (p) => `[peso ${p.peso}] ${p.ponto}`
      );
      const criteriosBase = q.gabarito?.criterios ?? [];
      // Junta pontos esperados + critérios qualitativos no mesmo array
      // (EssayQuestionBody renderiza como lista única dentro do gabarito).
      const criteriosUnificados =
        pontosFormatados.length > 0 || criteriosBase.length > 0
          ? [...pontosFormatados, ...criteriosBase]
          : undefined;

      return {
        id: `dissai-${i}`,
        pergunta: q.enunciado,
        opcoes: [],
        // ⚠️ correta=0 = sinaliza "anulada" no resto do app (isAnulada checa
        // Number(q.correta) === 0). Pra essay, usamos -1 = "sem gabarito
        // numérico, é dissertativa". Bug 2026-05-29: questões IA-geradas
        // dissertativas vinham com correta=0 e apareciam marcadas como Anulada.
        correta: -1,
        tipo: "essay",
        caso: q.caso,
        tema: q.tema || "Sem tema",
        banca: "IA Gerada",
        imagens: (q.imagens as Array<string | { url: string; legenda?: string }>) ?? [],
        tabelas: (q.tabelas as unknown[]) ?? [],
        gabarito_modelo: q.gabarito?.resposta_modelo ?? undefined,
        // direcionadores: undefined  ← intencional, evita spoiler do gabarito
        criterios: criteriosUnificados,
        competencias: q.gabarito?.armadilhas_comuns
      };
    });
  }, [raw]);

  // Estado das respostas no formato EssayResponseState (chave = qid).
  // Sincronizado pelo onEssayChange do QuestionCard quando o aluno digita
  // ou pede correção.
  const [essayResponses, setEssayResponses] = useState<Record<string, EssayResponseState>>({});

  useEffect(() => {
    // Hidrata tempos persistidos por questão + total acumulado.
    // Karol 2026-05-29: "tempo resetava a cada entrada". Backend agora
    // grava tempoMs em essayResponses[dissai-N].tempoMs.
    const nextTempos: Record<string, number> = {};
    let totalAcum = 0;
    for (let i = 0; i < raw.length; i++) {
      const r = raw[i];
      const qid = `dissai-${i}`;
      const t = r.tempoMs ?? 0;
      if (t > 0) {
        nextTempos[qid] = t;
        totalAcum += t;
      }
    }
    setEssayTempos(nextTempos);
    setTempoTotalMs(totalAcum);
  }, [raw]);

  // Persiste tempo da questão atual no servidor:
  // - Em todo q-switch (envia o tempo da que estamos SAINDO)
  // - No unmount/exitDissertativeWorkspace (último flush)
  // - Em beforeunload via keepalive
  // Valor ABSOLUTO em ms — evita conflito multi-tab. Backend reusa
  // a tabela essayResponses (mesmo bucket de resposta).
  const lastSavedTempoRef = useRef<Record<string, number>>({});
  function flushTempo(qid: string, ms: number, useKeepalive = false) {
    if (ms <= 0) return;
    // Evita flush se nada mudou desde o último envio.
    if (lastSavedTempoRef.current[qid] === ms) return;
    lastSavedTempoRef.current[qid] = ms;
    const qs = new URLSearchParams({ bankId, qid });
    try {
      void fetch(`/api/student/bank-essay?${qs.toString()}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tempoMs: ms }),
        credentials: "include",
        keepalive: useKeepalive
      });
    } catch {
      /* navegação pode bloquear no unload — segue a vida */
    }
  }

  // Flush no beforeunload: garante que o último valor chegue mesmo se a
  // aba fecha. Mesma técnica usada na resposta do essay (QuestionCard).
  useEffect(() => {
    function onUnload() {
      // Flush TODOS os qids com tempo > 0 que mudaram desde último save.
      const cur = currentQidRef.current;
      if (cur) {
        const ms = essayTempos[cur] ?? 0;
        flushTempo(cur, ms, true);
      }
    }
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("pagehide", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      window.removeEventListener("pagehide", onUnload);
      // Flush final ao desmontar (sair do workspace).
      onUnload();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId, essayTempos]);

  // Periodic flush a cada 15s pra não perder muito caso o browser feche
  // sem disparar beforeunload (mobile background, kill -9, etc.).
  useEffect(() => {
    const t = window.setInterval(() => {
      const cur = currentQidRef.current;
      if (!cur) return;
      const ms = essayTempos[cur] ?? 0;
      flushTempo(cur, ms);
    }, 15_000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId, essayTempos]);

  // Flush quando o aluno troca de questão — manda o tempo da questão
  // que estávamos antes.
  const prevQidRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevQidRef.current;
    const cur = currentQidRef.current;
    if (prev && prev !== cur) {
      const ms = essayTempos[prev] ?? 0;
      flushTempo(prev, ms);
    }
    prevQidRef.current = cur;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, raw]);

  useEffect(() => {
    // Hidrata do payload do backend ao recarregar
    const next: Record<string, EssayResponseState> = {};
    for (let i = 0; i < raw.length; i++) {
      const r = raw[i];
      const qid = `dissai-${i}`;
      if (r.respostaSalva || r.aiGrade) {
        next[qid] = {
          resposta: r.respostaSalva ?? "",
          nota: r.aiGrade?.score ?? null,
          feedback: r.aiGrade?.feedback ?? null,
          status: scoreToStatus(r.aiGrade?.score ?? null),
          gradedAt: r.aiGrade?.gradedAt ?? null,
          provider: null,
          model: null,
          // Propaga campos novos do AiGrade pro EssayGradeView renderizar
          // badge de modo + linha de desconto + lista de erros de norma culta.
          strictnessUsed: r.aiGrade?.strictnessUsed ?? null,
          descontoNormaCulta: r.aiGrade?.descontoNormaCulta ?? null,
          scoreSemDesconto: r.aiGrade?.scoreSemDesconto ?? null
        };
      }
    }
    setEssayResponses(next);
  }, [raw]);

  function onEssayChange(key: string, state: EssayResponseState) {
    setEssayResponses((prev) => ({ ...prev, [key]: state }));
    // Atualiza stats em runtime
    setStats((s) => {
      if (!s) return s;
      const ids = Object.keys({ ...essayResponses, [key]: state });
      const respondidas = ids.filter((k) => {
        const st = k === key ? state : essayResponses[k];
        return (st?.resposta ?? "").trim().length > 0;
      }).length;
      const avaliadas = ids.filter((k) => {
        const st = k === key ? state : essayResponses[k];
        return st?.gradedAt != null;
      }).length;
      const grades = ids
        .map((k) => (k === key ? state : essayResponses[k]))
        .filter((st) => st?.nota != null) as EssayResponseState[];
      const scoreMedia =
        grades.length === 0
          ? null
          : Math.round((grades.reduce((acc, g) => acc + (g.nota ?? 0), 0) / grades.length) * 10) / 10;
      return { ...s, respondidas, avaliadas, scoreMedia };
    });
  }

  function statusOf(q: QuestaoStudy): QListStatus {
    const st = essayResponses[q.id];
    if (!st) return "pending";
    if (st.nota != null) {
      const threshold = aiCorrectionPrefs.value.threshold;
      // 2 pontos abaixo do threshold = âmbar; senão = vermelho.
      const amberFloor = Math.max(0, threshold - 2);
      return st.nota >= threshold
        ? "correct"
        : st.nota >= amberFloor
          ? "answered"
          : "wrong";
    }
    return st.resposta && st.resposta.trim() ? "answered" : "pending";
  }

  async function finalize() {
    if (mode !== "prova") return;
    if (
      !confirm(
        "Finalizar simulação? Respostas ficam travadas e a IA vai corrigir todas as questões — pode levar de 30s a alguns minutos dependendo da quantidade. O gabarito também será liberado."
      )
    )
      return;
    setFinalizing(true);
    try {
      const respostas: Record<string, string> = {};
      for (let i = 0; i < questoesStudy.length; i++) {
        const qid = `dissai-${i}`;
        respostas[String(i)] = essayResponses[qid]?.resposta ?? "";
      }
      const r = await fetch(
        `/api/student/bank-dissertative?id=${encodeURIComponent(bankId)}&action=finalize`,
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ respostas })
        }
      );
      if (!r.ok) {
        alert("Falha ao finalizar. Tente de novo.");
        return;
      }
      // Sinaliza que estamos esperando o reload pra celebrar (se for o caso).
      setPendingCelebration(true);
      await reload();
    } finally {
      setFinalizing(false);
    }
  }

  // Flag que dispara celebração 1x quando `finalize()` rodou com sucesso E
  // os dados novos terminaram de carregar. Reseta após o trigger.
  const [pendingCelebration, setPendingCelebration] = useState(false);

  useEffect(() => {
    if (!pendingCelebration) return;
    // Espera o useEffect que popula essayResponses ter rodado (raw já mudou).
    const prefs = aiCorrectionPrefs.value;
    if (!prefs.celebrateDissertativeBlock) {
      setPendingCelebration(false);
      return;
    }
    const grades = raw
      .map((r) => r.aiGrade?.score)
      .filter((n): n is number => typeof n === "number");
    if (grades.length === 0) {
      setPendingCelebration(false);
      return;
    }
    const avg = grades.reduce((a, b) => a + b, 0) / grades.length;
    if (avg >= prefs.threshold) {
      try {
        fireCorrectAnimation();
      } catch {
        // animação falhar não deve quebrar o fluxo
      }
    }
    setPendingCelebration(false);
  }, [pendingCelebration, raw]);

  if (phase === "loading") {
    return (
      <div class="space-y-4">
        <Skeleton class="h-16 w-full" />
        <div class="flex gap-4">
          <Skeleton class="h-[400px] w-56 shrink-0" />
          <Skeleton class="h-[400px] flex-1" />
        </div>
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div class="space-y-3">
        <button
          type="button"
          onClick={exitDissertativeWorkspace}
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
        >
          <ArrowLeft className="size-3.5" />
          Voltar
        </button>
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="mr-2 inline size-4" />
          {errMsg}
        </div>
      </div>
    );
  }
  if (!bank) return null;

  const orderedAll = applyAgrupamento(questoesStudy, agrupamento);
  // Aplica filtros (status + tema) no ordered. Quando o set é vazio, considera
  // "todos" — assim o estado default não esconde nada.
  function statusKey(qq: QuestaoStudy): DissStatusKey {
    const st = essayResponses[qq.id];
    if (st?.nota != null || st?.gradedAt) return "avaliada";
    if (st?.resposta && st.resposta.trim()) return "respondida";
    return "pendente";
  }
  const ordered = orderedAll.filter((qq) => {
    if (filtroStatus.size > 0 && !filtroStatus.has(statusKey(qq))) return false;
    if (filtroTemas.size > 0) {
      const t = (qq.tema ?? "").trim() || "Sem tema";
      if (!filtroTemas.has(t)) return false;
    }
    return true;
  });
  // Mantém idx sincronizado com a ordem agrupada — currentIdx vai pro item ordered.
  const q = ordered[idx] ?? ordered[0] ?? null;
  // Mantém ref da questão visível atualizada — o setInterval do timer lê
  // daqui ao invés de depender do closure.
  currentQidRef.current = q?.id ?? null;

  const filterCount = filtroStatus.size + filtroTemas.size;
  const isFiltered = filterCount > 0;
  // Lista de temas únicos. Computada inline porque hooks só rodam antes dos
  // early returns — manter como cálculo direto evita violar a ordem.
  const temasSet = new Set<string>();
  for (const qq of questoesStudy) {
    const t = (qq.tema ?? "").trim() || "Sem tema";
    temasSet.add(t);
  }
  const temas: string[] = Array.from(temasSet).sort((a, b) =>
    a.localeCompare(b, "pt-BR")
  );
  if (!q) {
    return (
      <div class="space-y-3">
        <button
          type="button"
          onClick={exitDissertativeWorkspace}
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
        >
          <ArrowLeft className="size-3.5" />
          Voltar
        </button>
        <div class="rounded-md border border-dashed border-border bg-card/40 p-10 text-center text-sm text-muted-foreground">
          Sem questões dissertativas pra exibir.
        </div>
      </div>
    );
  }

  const side = uiPrefs.value.questionListSide;
  // Em modo prova, gabarito só libera depois de finalizar (showExplanation=false).
  const showExplanation = mode === "estudo" || finalized;

  return (
    <div class="space-y-5">
      {/* Overlay durante o finalize: bloqueia interação e avisa que a IA está
          corrigindo. Sem isso, o aluno pensa que travou e fecha a aba —
          perde o resultado. */}
      {finalizing && (
        <div class="fixed inset-0 z-[100] grid place-items-center bg-background/85 backdrop-blur-sm">
          <div class="mx-4 max-w-md space-y-4 rounded-lg border border-border bg-card px-8 py-7 text-center shadow-xl">
            <div class="mx-auto grid size-14 place-items-center rounded-full bg-fuchsia-500/15 text-fuchsia-700 ring-1 ring-fuchsia-500/30 dark:text-fuchsia-300">
              <Loader2 className="size-7 animate-spin" />
            </div>
            <div class="space-y-1.5">
              <h2 class="text-lg font-bold">Corrigindo sua prova com IA</h2>
              <p class="text-sm text-muted-foreground">
                Estou avaliando cada resposta dissertativa em paralelo. Pode
                levar de 30 segundos a alguns minutos dependendo do tamanho da
                prova.
              </p>
              <p class="text-xs italic text-muted-foreground">
                Não feche essa aba — você vai ver a nota e o feedback de cada
                questão quando terminar.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Banner de resultado: aparece quando a prova já foi finalizada,
          destaca a nota média + total avaliadas no topo. Pedido 2026-05-23
          ("foco no local onde mostra resultados"). */}
      {finalized && stats && stats.avaliadas > 0 && (
        <div class="rounded-md border-2 border-emerald-500/40 bg-emerald-500/5 px-5 py-4">
          <div class="flex flex-wrap items-center gap-4">
            <div class="grid size-14 place-items-center rounded-full bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300">
              <CheckCircle className="size-7" />
            </div>
            <div class="min-w-0 flex-1">
              <h2 class="text-base font-bold text-emerald-800 dark:text-emerald-200">
                Prova corrigida pela IA
              </h2>
              <p class="text-xs text-muted-foreground">
                {stats.avaliadas} de {stats.total} questões avaliadas
                {stats.respondidas > stats.avaliadas && (
                  <>
                    <span class="mx-1.5 opacity-50">·</span>
                    {stats.respondidas - stats.avaliadas} respondida
                    {stats.respondidas - stats.avaliadas !== 1 ? "s" : ""} sem
                    correção (você pode regerar pelo botão na questão)
                  </>
                )}
              </p>
            </div>
            {stats.scoreMedia != null && (
              <div class="text-right">
                <div class="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                  Nota média
                </div>
                <div class="font-bold tabular-nums text-emerald-800 dark:text-emerald-200">
                  <span class="text-3xl">
                    {stats.scoreMedia.toFixed(1).replace(".", ",")}
                  </span>
                  <span class="text-sm opacity-70"> / 10</span>
                </div>
              </div>
            )}
          </div>
          <p class="mt-3 text-xs text-emerald-900/80 dark:text-emerald-100/80">
            Use a lista lateral ou as setas pra revisar cada questão — você vê
            sua resposta, o feedback da IA com pontos atendidos/faltando, e o
            gabarito-modelo ao lado.
          </p>
        </div>
      )}

      <DissertativeHeader
        bank={bank}
        idx={idx}
        total={ordered.length}
        totalUnfiltered={orderedAll.length}
        isFiltered={isFiltered}
        filterCount={filterCount}
        stats={stats}
        mode={mode}
        finalized={finalized}
        finalizing={finalizing}
        tempoAtualMs={q ? essayTempos[q.id] ?? 0 : 0}
        tempoTotalMs={tempoTotalMs}
        onOpenFilters={() => setFiltersOpen(true)}
        onOpenMap={() => setMapOpen(true)}
        onOpenPerformance={() => setPerformanceOpen(true)}
        onBack={exitDissertativeWorkspace}
        onFinalize={finalize}
      />

      {filtersOpen && (
        <DissFiltersModal
          temas={temas}
          filtroStatus={filtroStatus}
          filtroTemas={filtroTemas}
          onApply={(st, tm) => {
            setFiltroStatus(st);
            setFiltroTemas(tm);
            setIdx(0);
            setFiltersOpen(false);
          }}
          onClose={() => setFiltersOpen(false)}
        />
      )}
      {mapOpen && (
        <DissMapModal
          questoes={orderedAll}
          statusOf={statusOf}
          numOf={(qq) => {
            const idxFonte = questoesStudy.findIndex((eq) => eq.id === qq.id);
            const n = (qq as any).numero_original;
            return n != null && n !== "" ? String(n) : idxFonte >= 0 ? String(idxFonte + 1) : "?";
          }}
          onPick={(qq) => {
            const visibleIdx = ordered.findIndex((x) => x.id === qq.id);
            if (visibleIdx >= 0) {
              setIdx(visibleIdx);
            } else {
              // Questão está filtrada: limpa filtros pra poder navegar.
              setFiltroStatus(new Set());
              setFiltroTemas(new Set());
              const allIdx = orderedAll.findIndex((x) => x.id === qq.id);
              setIdx(Math.max(0, allIdx));
            }
            setMapOpen(false);
          }}
          onClose={() => setMapOpen(false)}
        />
      )}
      {performanceOpen && (
        <DissPerformanceModal
          questoes={questoesStudy}
          essayResponses={essayResponses}
          stats={stats}
          onClose={() => setPerformanceOpen(false)}
        />
      )}

      {/* Aviso 2026-05-23: simulação dissertativa começa do zero. Espelha o
          aviso do ProvaStartModal pra não confundir o aluno que já fez o
          mesmo banco em Modo Estudo. Só mostra em prova não-finalizada e
          quando ainda não tem nenhuma resposta gradada (= primeira vez). */}
      {mode === "prova" && !finalized && stats && stats.avaliadas === 0 && (
        <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm">
          <div class="mb-1 flex items-center gap-2 font-bold text-amber-700 dark:text-amber-300">
            <AlertCircle className="size-4" />
            Simulação começa do zero
          </div>
          <p class="text-xs leading-relaxed text-amber-900/80 dark:text-amber-100/80">
            Suas respostas do <strong>Modo Estudo</strong> deste banco não
            entram aqui. Quando finalizar, a IA corrige todas as questões
            automaticamente.
          </p>
        </div>
      )}

      <div
        class={`flex flex-col gap-5 lg:items-start ${
          side === "right" ? "lg:flex-row-reverse" : "lg:flex-row"
        }`}
      >
        <QuestionListSidebar
          questoes={ordered}
          currentIdx={idx}
          onPick={(i) => setIdx(i)}
          statusOf={statusOf}
          tempoMsOf={(qq: any) => essayTempos[qq.id] ?? 0}
          agrupamento={agrupamento}
          onAgrupamentoChange={(a) => setAgrupamento(a === "fonte" ? "fonte" : "tema")}
          numOf={(q: any) => {
            const idxFonte = questoesStudy.findIndex((eq) => eq.id === q.id);
            const n = (q as any).numero_original;
            return n != null && n !== "" ? n : idxFonte >= 0 ? idxFonte + 1 : "?";
          }}
          title="Questões dissertativas"
          countLabel={`${ordered.length}`}
        />

        <div class="min-w-0 flex-1 space-y-5">
          <QuestionCard
            q={q}
            selected={undefined}
            feedback="answered"
            showExplanation={showExplanation}
            showStatusSummary
            bankId={bank.id}
            essayResponses={essayResponses}
            onEssayChange={onEssayChange}
            tempoMs={essayTempos[q.id] ?? 0}
          />

          <NavigationBar
            idx={idx}
            total={ordered.length}
            onPrev={() => setIdx(Math.max(0, idx - 1))}
            onNext={() => setIdx(Math.min(ordered.length - 1, idx + 1))}
          />
        </div>

        <AiAssistant
          question={q}
          respostaAluno={null}
          bankId={bank.id}
        />
      </div>
    </div>
  );
}

// ===== Subcomponentes =====

function DissertativeHeader({
  bank,
  idx,
  total,
  totalUnfiltered,
  isFiltered,
  filterCount,
  stats,
  mode,
  finalized,
  finalizing,
  tempoAtualMs,
  tempoTotalMs,
  onOpenFilters,
  onOpenMap,
  onOpenPerformance,
  onBack,
  onFinalize,
  onReset
}: {
  bank: { name: string; id: string };
  idx: number;
  total: number;
  totalUnfiltered: number;
  isFiltered: boolean;
  filterCount: number;
  stats: ApiResponse["stats"] | null;
  mode: DissertativeMode;
  finalized: boolean;
  finalizing: boolean;
  tempoAtualMs: number;
  tempoTotalMs: number;
  onOpenFilters: () => void;
  onOpenMap: () => void;
  onOpenPerformance: () => void;
  onBack: () => void;
  onFinalize: () => void;
  onReset?: () => void;
}) {
  // Subtitle igual ao StudyWorkspace — "Questão X de N" + badge do modo
  // ("Estudo · Dissertativa") + "Finalizada" quando aplicável.
  const subtitle = (
    <span class="inline-flex flex-wrap items-center gap-1.5">
      <span>
        Questão{" "}
        <span class="font-bold text-foreground">{Math.min(idx + 1, total)}</span>{" "}
        de <span class="font-bold text-foreground">{total}</span>
        {isFiltered && (
          <span class="ml-1.5 text-muted-foreground/70">
            (de {totalUnfiltered} no banco)
          </span>
        )}
      </span>
      <span class="inline-flex items-center gap-1 rounded-full bg-fuchsia-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300">
        <Sparkles className="size-3" />
        {mode === "estudo" ? "Estudo · Dissertativa" : "Simulação · Dissertativa"}
      </span>
      {finalized && (
        <span class="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
          <CheckCircle className="size-3" />
          Finalizada
        </span>
      )}
    </span>
  );

  // Summary — 2x2 grid de mini chips. Pra dissertativa as estatísticas que
  // fazem sentido são: Respondidas / Avaliadas / Pendentes / Nota média.
  // Layout idêntico ao PerformanceMiniSummary do StudyWorkspace pra alinhar
  // visualmente com o resto do app. [[project-equalize-workspace-headers]]
  const respondidas = stats?.respondidas ?? 0;
  const avaliadas = stats?.avaliadas ?? 0;
  const pendentes = Math.max(0, total - respondidas);
  const notaMedia = stats?.scoreMedia ?? null;
  const summary = (
    <div class="grid h-full grid-cols-2 grid-rows-2 gap-2 lg:min-w-[16rem]">
      <DissChipStat tone="emerald" label="Respond." value={respondidas} />
      <DissChipStat tone="primary" label="Avaliadas" value={avaliadas} />
      <DissChipStat tone="muted" label="Pend." value={pendentes} />
      <DissChipStat
        tone="primary"
        label="Nota média"
        value={notaMedia != null ? `${notaMedia}/10` : "—"}
      />
    </div>
  );

  // Actions — paridade com StudyWorkspace pra equalizar visualmente.
  // Karol 2026-05-29: "header dissertativa precisa de Filtros, Mapa,
  // Desempenho como tem no Estudo".
  const actions: WorkspaceHeaderAction[] = [
    {
      id: "filtros",
      icon: SlidersHorizontal,
      label: "Filtros",
      title: "Filtros",
      onClick: onOpenFilters,
      tone: filterCount > 0 ? "amber" : "neutral",
      badge: filterCount > 0 ? String(filterCount) : undefined
    },
    {
      id: "desempenho",
      icon: BarChart3,
      label: "Desempenho",
      title: "Ver desempenho",
      onClick: onOpenPerformance
    },
    {
      id: "mapa",
      icon: Grid3x3,
      label: "Mapa",
      title: "Mapa de questões",
      onClick: onOpenMap
    },
    {
      id: "lista",
      icon: List,
      label: "Lista",
      title: uiPrefs.value.questionListCollapsed
        ? "Mostrar lista de questões"
        : "Esconder lista de questões",
      onClick: () => toggleQuestionListCollapsed(),
      tone: !uiPrefs.value.questionListCollapsed ? "primary" : "neutral"
    }
  ];
  if (uiPrefs.value.aiAssistantEnabled) {
    actions.push({
      id: "ia",
      icon: Sparkles,
      label: "IA & notas",
      title: uiPrefs.value.aiAssistantOpen
        ? "Esconder assistente IA"
        : "Mostrar assistente IA",
      onClick: () => toggleAiAssistantOpen(),
      tone: uiPrefs.value.aiAssistantOpen ? "primary" : "neutral"
    });
  }
  if (onReset && !finalized) {
    actions.push({
      id: "recomecar",
      icon: RotateCcw,
      label: "Recomeçar",
      title: "Recomeçar — apaga respostas e correções",
      onClick: onReset,
      tone: "danger"
    });
  }
  if (mode === "prova" && !finalized) {
    actions.push({
      id: "finalizar",
      icon: finalizing ? Loader2 : Flag,
      label: "Finalizar",
      title: "Finalizar simulação",
      onClick: onFinalize,
      tone: "amber"
    });
  }

  return (
    <div class="space-y-2">
      {/* Voltar fora do WorkspaceHeader pra manter o breadcrumb leve.
          StudyWorkspace usa o mesmo padrão (Voltar implícito via shell). */}
      <button
        type="button"
        onClick={onBack}
        title="Voltar para o banco"
        aria-label="Voltar"
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
      >
        <ArrowLeft className="size-3.5" />
        Voltar
      </button>
      <WorkspaceHeader
        mode={mode === "prova" ? "prova" : "study"}
        icon={Pencil}
        iconTone="violet"
        title={bank.name}
        subtitle={subtitle}
        summary={summary}
        timers={
          total > 0 ? (
            <DissTimers atualMs={tempoAtualMs} totalMs={tempoTotalMs} />
          ) : undefined
        }
        actions={actions}
      />
    </div>
  );
}

/** Format ms → HH:MM:SS (ou MM:SS se < 1h). Mirror do StudyWorkspace. */
function formatHMS(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Cronômetros (Questão atual + Total) — visual idêntico ao StudyTimers. */
function DissTimers({ atualMs, totalMs }: { atualMs: number; totalMs: number }) {
  return (
    <div class="flex w-full flex-col gap-2 sm:w-auto sm:min-w-[160px]">
      <DissTimerChip
        label="Questão atual"
        tooltip="Tempo cronometrado nesta questão (esta sessão)"
        icon={Clock}
        value={formatHMS(atualMs)}
        emphasis="primary"
      />
      <DissTimerChip
        label="Total"
        tooltip="Tempo total cronometrado nas questões dissertativas (esta sessão)"
        icon={Hourglass}
        value={formatHMS(totalMs)}
      />
    </div>
  );
}

function DissTimerChip({
  label,
  tooltip,
  icon: Icon,
  value,
  emphasis
}: {
  label: string;
  tooltip: string;
  icon: typeof Clock;
  value: string;
  emphasis?: "primary";
}) {
  const tone =
    emphasis === "primary"
      ? "border-primary/40 bg-primary/5 text-foreground"
      : "border-border bg-background text-foreground/85";
  return (
    <span
      title={tooltip}
      class={`flex h-12 w-full items-center gap-2 rounded-md border px-3 text-sm leading-none ${tone}`}
    >
      <Icon className="size-4 shrink-0 text-primary" strokeWidth={2.25} />
      <span class="flex min-w-0 flex-col items-start gap-0.5">
        <span class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span class="font-bold tabular-nums">{value}</span>
      </span>
    </span>
  );
}

/** Mini-chip de estatística — espelha o MiniStat do StudyWorkspace pra
 *  garantir alinhamento visual quando os dois workspaces estão lado-a-lado. */
function DissChipStat({
  tone,
  label,
  value
}: {
  tone: "emerald" | "red" | "muted" | "primary";
  label: string;
  value: number | string;
}) {
  const toneClasses =
    tone === "emerald"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : tone === "red"
        ? "bg-red-500/10 text-red-600 dark:text-red-400"
        : tone === "primary"
          ? "bg-primary/10 text-primary"
          : "bg-muted/40 text-muted-foreground";
  return (
    <div
      class={`flex h-10 min-h-10 flex-col items-center justify-center gap-0.5 rounded-md border border-border px-2 py-1 ${toneClasses}`}
      title={label}
    >
      <span class="text-[9px] font-bold uppercase tracking-wide opacity-80 leading-none">
        {label}
      </span>
      <span class="text-sm font-bold leading-none text-foreground">{value}</span>
    </div>
  );
}

function NavigationBar({
  idx,
  total,
  onPrev,
  onNext
}: {
  idx: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <nav class="flex items-center justify-between gap-2 rounded-md border border-border bg-card/40 px-4 py-2.5">
      <button
        type="button"
        onClick={onPrev}
        disabled={idx === 0}
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
      >
        <ChevronLeft className="size-3.5" />
        Anterior
      </button>
      <span class="text-[11px] font-semibold text-muted-foreground">
        {idx + 1} / {total}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={idx === total - 1}
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
      >
        Próxima
        <ChevronRight className="size-3.5" />
      </button>
    </nav>
  );
}

function scoreToStatus(score: number | null): EssayResponseState["status"] {
  if (score == null) return null;
  if (score >= 8) return "EXCELENTE";
  if (score >= 6) return "BOM";
  if (score >= 4) return "REGULAR";
  return "INSUFICIENTE";
}

// ===== Modais (Filtros / Mapa / Desempenho) =====
// Versões enxutas, escopadas pra dissertativa. Não reusam os modais do
// StudyWorkspace porque aqueles dependem de BankStudyResponse com respostas
// MC e filtros estruturados — incompatível com o shape essay-only.

function DissModalShell({
  title,
  icon: Icon,
  children,
  onClose,
  widthClass = "max-w-xl"
}: {
  title: string;
  icon: typeof SlidersHorizontal;
  children: any;
  onClose: () => void;
  widthClass?: string;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class={`flex max-h-[90vh] w-full ${widthClass} flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div class="flex items-center gap-2">
            <Icon className="size-4 text-primary" />
            <h2 class="text-sm font-bold">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function DissFiltersModal({
  temas,
  filtroStatus,
  filtroTemas,
  onApply,
  onClose
}: {
  temas: string[];
  filtroStatus: Set<DissStatusKey>;
  filtroTemas: Set<string>;
  onApply: (status: Set<DissStatusKey>, temas: Set<string>) => void;
  onClose: () => void;
}) {
  const [st, setSt] = useState<Set<DissStatusKey>>(new Set(filtroStatus));
  const [tm, setTm] = useState<Set<string>>(new Set(filtroTemas));

  function toggleSt(k: DissStatusKey) {
    const next = new Set(st);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setSt(next);
  }
  function toggleTm(t: string) {
    const next = new Set(tm);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    setTm(next);
  }
  function limpar() {
    setSt(new Set());
    setTm(new Set());
  }

  const statusOptions: { key: DissStatusKey; label: string }[] = [
    { key: "pendente", label: "Pendentes" },
    { key: "respondida", label: "Respondidas (sem correção)" },
    { key: "avaliada", label: "Avaliadas pela IA" }
  ];

  return (
    <DissModalShell title="Filtros" icon={SlidersHorizontal} onClose={onClose}>
      <div class="space-y-5">
        <section>
          <h3 class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Status
          </h3>
          <div class="flex flex-wrap gap-2">
            {statusOptions.map((opt) => {
              const active = st.has(opt.key);
              return (
                <button
                  type="button"
                  onClick={() => toggleSt(opt.key)}
                  class={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background hover:bg-accent"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </section>

        {temas.length > 0 && (
          <section>
            <h3 class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Temas
            </h3>
            <div class="flex flex-wrap gap-2">
              {temas.map((t) => {
                const active = tm.has(t);
                return (
                  <button
                    type="button"
                    onClick={() => toggleTm(t)}
                    class={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      active
                        ? "border-primary bg-primary/15 text-primary"
                        : "border-border bg-background hover:bg-accent"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <div class="flex items-center justify-end gap-2 border-t border-border pt-4">
          <button
            type="button"
            onClick={limpar}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Limpar
          </button>
          <button
            type="button"
            onClick={() => onApply(st, tm)}
            class="rounded-md bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground hover:bg-primary/90"
          >
            Aplicar
          </button>
        </div>
      </div>
    </DissModalShell>
  );
}

function DissMapModal({
  questoes,
  statusOf,
  numOf,
  onPick,
  onClose
}: {
  questoes: QuestaoStudy[];
  statusOf: (q: QuestaoStudy) => QListStatus;
  numOf: (q: QuestaoStudy) => string;
  onPick: (q: QuestaoStudy) => void;
  onClose: () => void;
}) {
  function toneFor(st: QListStatus): string {
    if (st === "correct")
      return "border-emerald-500/50 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
    if (st === "wrong")
      return "border-red-500/50 bg-red-500/15 text-red-700 dark:text-red-300";
    if (st === "answered")
      return "border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300";
    return "border-border bg-background text-muted-foreground hover:bg-accent";
  }
  return (
    <DissModalShell
      title="Mapa de questões"
      icon={Grid3x3}
      onClose={onClose}
      widthClass="max-w-2xl"
    >
      <div class="mb-4 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <LegendDot tone="emerald" label="Nota acima do alvo" />
        <LegendDot tone="amber" label="Respondida / parcial" />
        <LegendDot tone="red" label="Abaixo do alvo" />
        <LegendDot tone="muted" label="Pendente" />
      </div>
      <div class="grid grid-cols-6 gap-2 sm:grid-cols-8 md:grid-cols-10">
        {questoes.map((qq) => {
          const st = statusOf(qq);
          return (
            <button
              key={qq.id}
              type="button"
              onClick={() => onPick(qq)}
              title={`Q${numOf(qq)} — ${qq.tema || "Sem tema"}`}
              class={`grid aspect-square place-items-center rounded-md border text-[11px] font-bold tabular-nums transition ${toneFor(st)}`}
            >
              {numOf(qq)}
            </button>
          );
        })}
      </div>
    </DissModalShell>
  );
}

function LegendDot({
  tone,
  label
}: {
  tone: "emerald" | "amber" | "red" | "muted";
  label: string;
}) {
  const cls =
    tone === "emerald"
      ? "bg-emerald-500/40 border-emerald-500/60"
      : tone === "amber"
        ? "bg-amber-500/40 border-amber-500/60"
        : tone === "red"
          ? "bg-red-500/40 border-red-500/60"
          : "bg-muted/40 border-border";
  return (
    <span class="inline-flex items-center gap-1.5">
      <span class={`inline-block size-3 rounded border ${cls}`} />
      {label}
    </span>
  );
}

function DissPerformanceModal({
  questoes,
  essayResponses,
  stats,
  onClose
}: {
  questoes: QuestaoStudy[];
  essayResponses: Record<string, EssayResponseState>;
  stats: ApiResponse["stats"] | null;
  onClose: () => void;
}) {
  // Distribuição de notas em buckets (0-4 / 4-6 / 6-8 / 8-10).
  const buckets = [
    { label: "0–3,9", min: 0, max: 4, tone: "red" as const, count: 0 },
    { label: "4–5,9", min: 4, max: 6, tone: "amber" as const, count: 0 },
    { label: "6–7,9", min: 6, max: 8, tone: "amber" as const, count: 0 },
    { label: "8–10", min: 8, max: 10.01, tone: "emerald" as const, count: 0 }
  ];
  const notas: number[] = [];
  for (const qq of questoes) {
    const st = essayResponses[qq.id];
    if (st?.nota != null) {
      notas.push(st.nota);
      for (const b of buckets) {
        if (st.nota >= b.min && st.nota < b.max) {
          b.count++;
          break;
        }
      }
    }
  }
  const maxBucket = Math.max(1, ...buckets.map((b) => b.count));

  // Nota por tema (média) — útil pra identificar onde o aluno fica fraco.
  const porTema = new Map<string, { soma: number; n: number }>();
  for (const qq of questoes) {
    const st = essayResponses[qq.id];
    if (st?.nota != null) {
      const t = (qq.tema ?? "").trim() || "Sem tema";
      const cur = porTema.get(t) ?? { soma: 0, n: 0 };
      cur.soma += st.nota;
      cur.n += 1;
      porTema.set(t, cur);
    }
  }
  const temasOrdenados = Array.from(porTema.entries())
    .map(([tema, v]) => ({ tema, media: v.soma / v.n, n: v.n }))
    .sort((a, b) => a.media - b.media);

  return (
    <DissModalShell
      title="Desempenho"
      icon={BarChart3}
      onClose={onClose}
      widthClass="max-w-2xl"
    >
      <div class="space-y-6">
        <section class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <PerfStat
            label="Total"
            value={String(stats?.total ?? questoes.length)}
          />
          <PerfStat label="Respondidas" value={String(stats?.respondidas ?? 0)} />
          <PerfStat label="Avaliadas" value={String(stats?.avaliadas ?? 0)} />
          <PerfStat
            label="Nota média"
            value={
              stats?.scoreMedia != null
                ? stats.scoreMedia.toFixed(1).replace(".", ",")
                : "—"
            }
            tone="primary"
          />
        </section>

        <section>
          <h3 class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Distribuição de notas
          </h3>
          {notas.length === 0 ? (
            <p class="text-sm italic text-muted-foreground">
              Sem questões avaliadas ainda.
            </p>
          ) : (
            <div class="space-y-2">
              {buckets.map((b) => {
                const pct = Math.round((b.count / maxBucket) * 100);
                const barCls =
                  b.tone === "emerald"
                    ? "bg-emerald-500/70"
                    : b.tone === "amber"
                      ? "bg-amber-500/70"
                      : "bg-red-500/70";
                return (
                  <div class="flex items-center gap-3">
                    <span class="w-16 text-xs font-semibold tabular-nums text-muted-foreground">
                      {b.label}
                    </span>
                    <div class="relative h-6 flex-1 overflow-hidden rounded-md bg-muted/40">
                      <div
                        class={`absolute inset-y-0 left-0 ${barCls}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span class="w-8 text-right text-xs font-bold tabular-nums">
                      {b.count}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {temasOrdenados.length > 0 && (
          <section>
            <h3 class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Média por tema (do pior pro melhor)
            </h3>
            <ul class="divide-y divide-border rounded-md border border-border">
              {temasOrdenados.map((t) => {
                const tone =
                  t.media >= 8
                    ? "text-emerald-700 dark:text-emerald-300"
                    : t.media >= 6
                      ? "text-amber-700 dark:text-amber-300"
                      : "text-red-700 dark:text-red-300";
                return (
                  <li class="flex items-center justify-between gap-3 px-3 py-2">
                    <span class="truncate text-sm">{t.tema}</span>
                    <span class="flex items-center gap-2 text-xs">
                      <span class="text-muted-foreground">
                        {t.n} {t.n === 1 ? "questão" : "questões"}
                      </span>
                      <span class={`font-bold tabular-nums ${tone}`}>
                        {t.media.toFixed(1).replace(".", ",")}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </DissModalShell>
  );
}

function PerfStat({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone?: "primary";
}) {
  const valCls = tone === "primary" ? "text-primary" : "text-foreground";
  return (
    <div class="rounded-md border border-border bg-background/60 px-3 py-2">
      <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div class={`text-xl font-bold tabular-nums ${valCls}`}>{value}</div>
    </div>
  );
}
